import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';

/** Un bloque de respuesta: un párrafo (string) o una lista de viñetas. */
export type FAQAnswerBlock = string | { items: string[] };

export interface FAQItem {
	question: string;
	/**
	 * Respuesta. Un array se pinta como bloques (párrafos y/o listas). Dentro de
	 * cualquier texto, `**...**` marca negrita inline.
	 */
	answer: string | FAQAnswerBlock[];
	/** Etiqueta de un botón de acción bajo la respuesta (p. ej. abrir el asistente). */
	cta?: string;
}

/** Convierte `**texto**` en <strong> dentro de una cadena, dejando el resto igual. */
const renderInline = (text: string) =>
	text.split(/(\*\*.+?\*\*)/g).map((part, i) => {
		const bold = /^\*\*(.+)\*\*$/.exec(part);
		return bold ? (
			<strong key={i} className="font-semibold text-foreground">
				{bold[1]}
			</strong>
		) : (
			<React.Fragment key={i}>{part}</React.Fragment>
		);
	});

interface TrustFAQProps {
	items: FAQItem[];
	/** Índice seleccionado/abierto al montar (default: 0). */
	defaultOpenIndex?: number;
	/** Handler del CTA de un item (p. ej. abrir el asistente Noé). */
	onCtaClick?: () => void;
}

/**
 * FAQ maestro-detalle en desktop (lista de preguntas a la izquierda + respuesta en un
 * panel de altura reservada a la derecha → al cambiar de pregunta no se mueve nada del
 * resto) y acordeón clásico en mobile. En cards blancas para leer mejor.
 */
const TrustFAQ: React.FC<TrustFAQProps> = ({ items, defaultOpenIndex = 0, onCtaClick }) => {
	const [active, setActive] = useState<number>(defaultOpenIndex);

	const shown = active < 0 ? 0 : active;
	const shownItem = items[shown];

	/** Pinta la respuesta como uno o varios bloques (párrafos y/o listas). */
	const answerBlocks = (answer: string | FAQAnswerBlock[], firstMarginClass: string) => {
		const blocks: FAQAnswerBlock[] = Array.isArray(answer) ? answer : [answer];
		return blocks.map((block, i) => {
			const margin = i === 0 ? firstMarginClass : 'mt-3';
			return typeof block === 'string' ? (
				<p key={i} className={cn('text-sm leading-relaxed text-muted-foreground', margin)}>
					{renderInline(block)}
				</p>
			) : (
				<ul
					key={i}
					className={cn(
						'list-disc space-y-1 pl-5 text-sm leading-relaxed text-muted-foreground',
						margin
					)}
				>
					{block.items.map((item, j) => (
						<li key={j}>{renderInline(item)}</li>
					))}
				</ul>
			);
		});
	};

	const ctaButton = (item: FAQItem) =>
		item.cta && onCtaClick ? (
			<button
				type="button"
				onClick={onCtaClick}
				className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-4 py-2 text-sm font-medium text-primary transition-colors hover:bg-primary/15"
			>
				{item.cta}
			</button>
		) : null;

	return (
		<>
			{/* Desktop: maestro-detalle (no salta nada al cambiar de pregunta) */}
			<div className="surface-card hidden overflow-hidden lg:grid lg:grid-cols-5">
				<ul className="border-r border-border/40 p-2 lg:col-span-2">
					{items.map((item, i) => (
						<li key={i}>
							<button
								type="button"
								onClick={() => setActive(i)}
								className={cn(
									'w-full rounded-lg px-3 py-2.5 text-left text-sm transition-colors',
									i === shown
										? 'bg-primary/5 font-medium text-primary'
										: 'text-foreground hover:bg-muted/40'
								)}
							>
								{item.question}
							</button>
						</li>
					))}
				</ul>
				<div className="min-h-52 bg-[hsl(150_15%_98.5%)] p-5 lg:col-span-3">
					<p className="text-sm font-semibold">{shownItem.question}</p>
					{answerBlocks(shownItem.answer, 'mt-2')}
					{ctaButton(shownItem)}
				</div>
			</div>

			{/* Mobile: acordeón en cards blancas */}
			<div className="space-y-2 lg:hidden">
				{items.map((item, i) => {
					const isOpen = i === active;
					return (
						<div key={i} className="surface-card overflow-hidden">
							<button
								type="button"
								aria-expanded={isOpen}
								onClick={() => setActive(isOpen ? -1 : i)}
								className="flex w-full items-center justify-between gap-3 p-4 text-left"
							>
								<span className="text-sm font-medium">{item.question}</span>
								<ChevronDown
									className={cn(
										'h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200',
										isOpen && 'rotate-180'
									)}
								/>
							</button>
							<div
								className={cn(
									'grid transition-all duration-200 ease-out',
									isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
								)}
							>
								<div className="overflow-hidden">
									<div className="bg-[hsl(150_15%_98.5%)] px-4 pb-4 pt-1">
										{answerBlocks(item.answer, '')}
										{ctaButton(item)}
									</div>
								</div>
							</div>
						</div>
					);
				})}
			</div>
		</>
	);
};

export default TrustFAQ;
